import { UploadFileTypeEnum } from 'api-hooks/upload/upload.model';
import { useGetUploadFileParam } from 'api-hooks/upload/upload.mutation';
import Delete from 'baseui/icon/delete';
import { Spinner } from 'baseui/spinner';
import { ResizeImage } from 'common/helpers/image';
import useDialog from 'hooks/use-dialog';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useDropzone } from 'react-dropzone';

import { ACCEPTED_FILE } from './main-file-picker';
import PreviewImageDialog from './preview-image-dialog';
import {
  PreviewContainer,
  ImagePreviewContainer,
  ImagePreview,
  RemoveImage,
  EditComponent,
  LoadingContainer,
  FilePreview,
} from './styles';
import { onDrop } from './utils';
import Text from '../text';

export interface PreviewOptions {
  id?: string;
  name?: string;
  disabled?: boolean;
  preview: string;
  onPicked: (files: { filename: string; fileUrl: string }[]) => void;
  onDelete?: () => void;
  accept?: string;
  isError?: boolean;
  resizeImage?: boolean;
  resizeWidth?: number;
  resizeHeight?: number;
  isMultiple?: boolean;
  fileType: UploadFileTypeEnum;
}

const checkExtension = (url) => {
  const splits = url?.split('?')[0].split('.');
  const extension = splits[splits?.length - 1]?.toLowerCase();
  const blobUrl = url.startsWith('blob:')
    ? url?.replaceAll(`.${extension}`, '')
    : url;

  return { extension, splits, blobUrl };
};

export function PreviewContent({ url }) {
  // const splits = url?.split('?')[0].split('.');
  // const extension = splits[splits?.length - 1]?.toLowerCase();
  const { extension, splits } = checkExtension(url);

  const currentUrl = url.startsWith('blob:')
    ? splits.slice(0, splits.length - 1).join('.')
    : url;

  if (['jpeg', 'jpg', 'png'].includes(extension)) {
    return <ImagePreview src={currentUrl} alt={extension} />;
  }

  if (['pdf', 'xlsx', 'docx', 'doc', 'xls'].includes(extension)) {
    const Icon = (extension) => {
      switch (extension.extension) {
        case 'xls':
        case 'xlsx':
          return (
            <FilePreview
              src="/assets/file-excel.png"
              alt={extension.extension}
            />
          );
        case 'doc':
        case 'docx':
          return (
            <FilePreview
              src="/assets/file-word.png"
              alt={extension.extension}
            />
          );
        case 'pdf':
          return (
            <FilePreview src="/assets/file-pdf.png" alt={extension.extension} />
          );
        default:
          return (
            <FilePreview
              src="/assets/global-file.png"
              alt={extension.extension}
            />
          );
      }
    };
    return (
      <PreviewContainer>
        <Icon extension={extension} />
      </PreviewContainer>
    );
  }

  return <FilePreview src="/assets/global-file.png" alt={extension} />;
}

export default function PreviewPicker(props: PreviewOptions) {
  const {
    onPicked,
    onDelete,
    preview,
    disabled,
    resizeImage,
    resizeWidth,
    resizeHeight,
    isMultiple,
    fileType,
  } = props;
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = React.useState(false);
  const { mutateAsync: uploadFileParam } = useGetUploadFileParam();
  const dialog = useDialog();

  const ResizeImageFunc = React.useCallback(
    async (file, type) => {
      const result: File = (await ResizeImage({
        imageToResize: file,
        width: resizeWidth!,
        height: resizeHeight!,
        type,
      })) as File;
      return result;
    },
    [resizeHeight, resizeWidth],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (files) =>
      onDrop({
        acceptedFiles: files,
        onPicked,
        ResizeImageFunc,
        setIsUploading,
        uploadFileParam,
        resizeImage,
        isMultiple,
        fileType,
      }),
  });

  //@ts-ignore
  const { onClick: openFilePicker, ...rootProps } = getRootProps({
    refKey: 'innerRef',
  });

  const onRootClick = React.useCallback(
    (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (!isUploading) {
        const { extension, blobUrl } = checkExtension(preview);

        if (['jpeg', 'jpg', 'png'].includes(extension)) {
          dialog.showCustom({
            render: (onClose) => (
              <PreviewImageDialog {...{ onClose, url: blobUrl }} />
            ),
          });
        } else {
          window.open(blobUrl);
        }
      }
    },
    [dialog, isUploading, preview],
  );

  return (
    <PreviewContainer onClick={onRootClick} {...rootProps}>
      <ImagePreviewContainer>
        {isUploading ? (
          <LoadingContainer>
            <Spinner $size={20} $color="primary" />
          </LoadingContainer>
        ) : (
          <>
            <PreviewContent url={preview} />
            {!disabled && (
              <>
                <RemoveImage
                  onClick={(e) => {
                    !!onDelete && onDelete();
                    e.stopPropagation();
                  }}
                >
                  <Delete />
                </RemoveImage>
                <EditComponent
                  onClick={(e) => {
                    openFilePicker && openFilePicker(e);
                    e.stopPropagation();
                  }}
                >
                  <Text variant="LabelSmall" $style={{ color: 'white' }}>
                    {t('common:edit')}
                  </Text>
                </EditComponent>
              </>
            )}
            <input
              {...getInputProps()}
              id={props.id}
              name={props.name}
              accept={ACCEPTED_FILE}
              disabled={disabled}
            />
          </>
        )}
      </ImagePreviewContainer>
    </PreviewContainer>
  );
}
